import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { CircleCheck } from 'lucide-react';

import { Input } from '@ui/components/ui/input';
import { DependencyPackage, STATUS_META } from './sources/types';

type Filter = 'all' | DependencyPackage['currency'];

// Unsupported to current, so the chips read in the same order as the list.
const FILTERS: { key: Filter; labelKey: string }[] = [
  { key: 'all', labelKey: 'depAll' },
  { key: 'unsupported', labelKey: 'depUnsupported' },
  { key: 'behind', labelKey: 'depBehind' },
  { key: 'current', labelKey: 'depCurrent' },
];

const CURRENCY_STATUS = { unsupported: 'risk', behind: 'watch', current: 'good' } as const;

// Fixed tracks, not `auto`: header and rows are separate grids, so `auto` would size each to its own text.
const ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_9rem] sm:grid-cols-[minmax(0,1fr)_9rem_8.5rem_11rem] items-center gap-x-3';

/** Every graded package, filterable by group and searchable by name, unsupported first. */
export const DependencyList = ({ packages }: { packages: DependencyPackage[] }): JSX.Element => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [atBottom, setAtBottom] = useState(false); // hides the scroll fade once fully scrolled / not overflowing

  const needle = query.trim().toLowerCase();
  const shown = packages.filter(
    pkg => (filter === 'all' || pkg.currency === filter) && pkg.name.toLowerCase().includes(needle),
  );
  const countOf = (key: Filter): number =>
    key === 'all' ? packages.length : packages.filter(pkg => pkg.currency === key).length;

  const updateFade = (el: HTMLDivElement | null): void => {
    if (el) setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 8);
  };
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [filter, needle]);
  useEffect(() => {
    updateFade(scrollRef.current);
  }, [packages, filter, needle]);

  const isAllCurrent =
    filter === 'all' &&
    !needle &&
    packages.every(pkg => pkg.currency === 'current' && pkg.latestVersion === pkg.versionInUse);

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={t('productDashboard.depSearch')}
          aria-label={t('productDashboard.depSearch')}
          className="min-w-40 flex-1 print:hidden"
        />
        <div className="flex flex-wrap gap-1.5 print:hidden" role="group" aria-label={t('productDashboard.depFilter')}>
          {FILTERS.map(({ key, labelKey }) => (
            <button
              key={key}
              type="button"
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
              className={cx(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors duration-200',
                filter === key
                  ? 'border-foreground/25 bg-muted text-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted/60',
              )}
            >
              {key !== 'all' && (
                <span
                  aria-hidden
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: STATUS_META[CURRENCY_STATUS[key]].stroke }}
                />
              )}
              {t(`productDashboard.${labelKey}`)}
              <span className="tabular-nums">{countOf(key)}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className={cx(ROW_GRID, 'mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground')}>
          <p>{t('productDashboard.depPackage')}</p>
          <p className="hidden text-right sm:block">{t('productDashboard.depVersion')}</p>
          <p className="hidden text-right sm:block">{t('productDashboard.newerOutFor')}</p>
          <p className="text-right">{t('productDashboard.status')}</p>
        </div>
        <div className="relative">
          <div
            ref={scrollRef}
            onScroll={event => updateFade(event.currentTarget)}
            // One height for every product and filter, so the card never changes size.
            className="h-60 overflow-y-auto pr-1 print:h-auto print:overflow-visible"
          >
            {isAllCurrent && (
              <p className="flex items-center gap-2 pb-2 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                <CircleCheck className="size-4 shrink-0" />
                {t('productDashboard.everythingUpToDate')}
              </p>
            )}
            {shown.map(pkg => (
              <div key={pkg.name} className={cx(ROW_GRID, 'border-b border-border/60 py-1.5 text-sm last:border-b-0')}>
                <span className="min-w-0">
                  <span className="block truncate text-foreground">{pkg.name}</span>
                  {pkg.usedAt && <span className="block text-xs text-muted-foreground">{pkg.usedAt}</span>}
                  {pkg.newerOutFor && (
                    <span className="block text-xs tabular-nums text-muted-foreground sm:hidden">
                      {t('productDashboard.newerOutForDuration', { duration: pkg.newerOutFor })}
                    </span>
                  )}
                </span>
                <span className="hidden truncate text-right font-mono text-xs text-muted-foreground sm:block">
                  {pkg.latestVersion === pkg.versionInUse ? (
                    pkg.versionInUse
                  ) : (
                    <>
                      {pkg.versionInUse} → <span className="text-foreground">{pkg.latestVersion}</span>
                    </>
                  )}
                </span>
                <span className="hidden whitespace-nowrap text-right tabular-nums text-foreground sm:block">
                  {pkg.newerOutFor}
                </span>
                {/* The colour sits on the reason, because the reason is what was judged. */}
                <span
                  className={cx(
                    // Wraps rather than clips: the reason is the fact a client reads, so all of it must show.
                    'break-words text-right font-medium',
                    STATUS_META[CURRENCY_STATUS[pkg.currency]].text,
                  )}
                >
                  {pkg.status}
                </span>
              </div>
            ))}
            {!shown.length && (
              <p className="py-3 text-sm text-muted-foreground">
                {needle
                  ? t('productDashboard.depNoMatch', { query: query.trim() })
                  : t('productDashboard.depGroupEmpty')}
              </p>
            )}
          </div>
          {/* Bottom fade: there is more to scroll; gone once at the bottom. */}
          <div
            className={cx(
              'pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-card to-transparent transition-opacity duration-200 print:hidden',
              atBottom && 'opacity-0',
            )}
          />
        </div>
      </div>
    </div>
  );
};

/** What the card measures, when it was checked, and how many packages it could not check. */
export const DependenciesCaption = ({
  checkedOn,
  uncheckedCount,
}: {
  checkedOn: string | null;
  uncheckedCount: number;
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <p className="mt-5 text-xs text-muted-foreground">
      {t('productDashboard.dependenciesCaption')}
      {checkedOn && ` ${t('productDashboard.dependenciesCheckedOn', { date: checkedOn })}`}
      {uncheckedCount > 0 && ` ${t('productDashboard.dependenciesUnchecked', { count: uncheckedCount })}`}
    </p>
  );
};
